import type { Rng } from '../core/rng';
import { registerPassive } from '../sim/passives';
import { registerStatus } from '../sim/status';
import { applyStatus } from '../sim/statusSystem';
import { STAGGER_POINTS, isBossGrade } from '../sim/types';
import { type Rarity, type StoneItem, type StoneLine, newUid, registerSigilBooks, stonePoints } from './items';

// ======================================================================
// SIGILS (engravings): build-defining passives. 5 points per level, max 3.
// ======================================================================

export interface SigilDef {
  id: string;
  name: string;
  negative?: boolean;
  /** Text per level 1..3. */
  levels: [string, string, string];
  /** Static stat bonuses per level (applied by the stat builder). */
  stats?: (lvl: number) => SigilStatMods;
}

export interface SigilStatMods {
  atkPct?: number;
  critRate?: number;
  critDmg?: number;
  atkSpeed?: number;
  moveSpeed?: number;
  defensePct?: number;
  healingReceived?: number;
  dmgTaken?: number;
  cdr?: number;
}

const L = (a: number, b: number, c: number) => (lvl: number) => [0, a, b, c][Math.max(0, Math.min(3, lvl))] as number;

export const SIGILS: Record<string, SigilDef> = {
  vendetta: { id: 'vendetta', name: 'Vendetta', levels: ['Damage to bosses +4%; damage taken +20%', 'Damage to bosses +10%; damage taken +20%', 'Damage to bosses +20%; damage taken +20%'], stats: () => ({ dmgTaken: 0.2 }) },
  hexed_idol: { id: 'hexed_idol', name: 'Hexed Idol', levels: ['Attack +3%; healing received -25%', 'Attack +8%; healing received -25%', 'Attack +16%; healing received -25%'], stats: (l) => ({ atkPct: L(0.03, 0.08, 0.16)(l), healingReceived: -0.25 }) },
  brutal_edge: { id: 'brutal_edge', name: 'Brutal Edge', levels: ['Crit damage +10%; 10% chance to deal -20% damage', 'Crit damage +25%; 10% chance to deal -20% damage', 'Crit damage +50%; 10% chance to deal -20% damage'], stats: (l) => ({ critDmg: L(0.1, 0.25, 0.5)(l) }) },
  battle_fervor: { id: 'battle_fervor', name: 'Battle Fervor', levels: ['Each skill cast: attack +0.3% (6 stacks); at max stacks crit rate +5%', 'Each skill cast: attack +0.6%; at max crit +10%', 'Each skill cast: attack +1%; at max crit +15%'] },
  steady_hand: { id: 'steady_hand', name: 'Steady Hand', levels: ['Non-positional damage +3%', 'Non-positional damage +8%', 'Non-positional damage +16%'] },
  ambusher: { id: 'ambusher', name: 'Ambusher', levels: ['Back attack damage +5%', 'Back attack damage +12%', 'Back attack damage +25%'] },
  brawler: { id: 'brawler', name: 'Brawler', levels: ['Head attack damage +5%', 'Head attack damage +12%', 'Head attack damage +25%'] },
  quickening: { id: 'quickening', name: 'Quickening', levels: ['Attack & move speed +3%', 'Attack & move speed +8%', 'Attack & move speed +15%'], stats: (l) => ({ atkSpeed: L(0.03, 0.08, 0.15)(l), moveSpeed: L(0.03, 0.08, 0.15)(l) }) },
  overcharge: { id: 'overcharge', name: 'Overcharge', levels: ['Charge & holding skill damage +4%', 'Charge & holding skill damage +10%', 'Charge & holding skill damage +20%'] },
  bulwark: { id: 'bulwark', name: 'Bulwark', levels: ['Damage +3% while shielded', 'Damage +8% while shielded', 'Damage +16% while shielded'] },
  precision: { id: 'precision', name: 'Precision', levels: ['Crit rate +4%; crit damage -12%', 'Crit rate +10%; crit damage -12%', 'Crit rate +20%; crit damage -12%'], stats: (l) => ({ critRate: L(0.04, 0.1, 0.2)(l), critDmg: -0.12 }) },
  apex_mastery: { id: 'apex_mastery', name: 'Apex Mastery', levels: ['Apex Art cooldown -10%, +1 use', 'Apex Art cooldown -25%, +2 uses', 'Apex Art cooldown -50%, +3 uses'] },
  ironhide: { id: 'ironhide', name: 'Ironhide', levels: ['Defence +20%', 'Defence +50%', 'Defence +100%'], stats: (l) => ({ defensePct: L(0.2, 0.5, 1)(l) }) },
  pressure_points: { id: 'pressure_points', name: 'Pressure Points', levels: ['Stagger +6%', 'Stagger +18%', 'Stagger +36%'] },
  composure: { id: 'composure', name: 'Composure', levels: ['Damage +3% while HP ≥ 80%', 'Damage +8% while HP ≥ 80%', 'Damage +16% while HP ≥ 80%'] },
  // Negative sigils rolled on rune stones.
  atk_down: { id: 'atk_down', name: 'Attack Reduction', negative: true, levels: ['Attack -2%', 'Attack -4%', 'Attack -6%'], stats: (l) => ({ atkPct: -L(0.02, 0.04, 0.06)(l) }) },
  def_down: { id: 'def_down', name: 'Defence Reduction', negative: true, levels: ['Defence -10%', 'Defence -20%', 'Defence -30%'], stats: (l) => ({ defensePct: -L(0.1, 0.2, 0.3)(l) }) },
  speed_down: { id: 'speed_down', name: 'Attack Speed Reduction', negative: true, levels: ['Attack speed -2%', 'Attack speed -4%', 'Attack speed -6%'], stats: (l) => ({ atkSpeed: -L(0.02, 0.04, 0.06)(l) }) },
  move_down: { id: 'move_down', name: 'Move Speed Reduction', negative: true, levels: ['Move speed -2%', 'Move speed -4%', 'Move speed -6%'], stats: (l) => ({ moveSpeed: -L(0.02, 0.04, 0.06)(l) }) },
};

export const COMBAT_SIGILS = Object.values(SIGILS).filter((s) => !s.negative).map((s) => s.id);
registerSigilBooks(Object.values(SIGILS).filter((s) => !s.negative));
export const NEGATIVE_SIGILS = Object.values(SIGILS).filter((s) => s.negative).map((s) => s.id);
export const MAX_ACTIVE_SIGILS = 5;
export const sigilLevel = (pts: number): number => Math.min(3, Math.floor(pts / 5));

// Runtime hooks for sigils that depend on combat context.
registerPassive('vendetta', (lvl) => ({
  outDamage: ({ target }) => (target.monster && isBossGrade(target.monster.grade) ? 1 + L(0.04, 0.1, 0.2)(lvl) : 1),
}));
registerPassive('brutal_edge', () => ({ outDamage: ({ crit }) => (!crit && Math.random() < 0.1 ? 0.8 : 1) }));
for (const lvl of [1, 2, 3]) {
  registerStatus({ id: `fervor_${lvl}`, name: 'Battle Fervor', kind: 'buff', icon: 'fervor', description: 'Attack increased per stack; crit rate up at 6 stacks.', stacking: 'stack', maxStacks: 6, mods: { atkPct: L(0.003, 0.006, 0.01)(lvl) }, color: 0xff9040 });
}
registerPassive('battle_fervor', (lvl) => ({
  onCast: (world, a) => applyStatus(world, a, { id: `fervor_${lvl}`, duration: 6 }, a.id, 0),
  critRate: (src) => ((src.getStatus(`fervor_${lvl}`)?.stacks ?? 0) >= 6 ? L(0.05, 0.1, 0.15)(lvl) : 0),
}));
registerPassive('steady_hand', (lvl) => ({ outDamage: ({ back, head }) => (!back && !head ? 1 + L(0.03, 0.08, 0.16)(lvl) : 1) }));
registerPassive('ambusher', (lvl) => ({ outDamage: ({ back }) => (back ? 1 + L(0.05, 0.12, 0.25)(lvl) : 1) }));
registerPassive('brawler', (lvl) => ({ outDamage: ({ head }) => (head ? 1 + L(0.05, 0.12, 0.25)(lvl) : 1) }));
registerPassive('overcharge', (lvl) => ({ outDamage: ({ skill }) => (skill && (skill.castType === 'charge' || skill.castType === 'holding') ? 1 + L(0.04, 0.1, 0.2)(lvl) : 1) }));
registerPassive('bulwark', (lvl) => ({ outDamage: ({ src }) => (src.shield > 0 ? 1 + L(0.03, 0.08, 0.16)(lvl) : 1) }));
registerPassive('composure', (lvl) => ({ outDamage: ({ src }) => (src.hpPct >= 0.8 ? 1 + L(0.03, 0.08, 0.16)(lvl) : 1) }));
registerPassive('pressure_points', (lvl) => ({ staggerMult: () => 1 + L(0.06, 0.18, 0.36)(lvl) }));
registerPassive('apex_mastery', (lvl) => ({ cooldownMult: (s) => (s.kind === 'awakening' ? 1 - L(0.1, 0.25, 0.5)(lvl) : 1) }));
void STAGGER_POINTS;

// ======================================================================
// RUNE STONES (ability stones): etch 3 lines (2 sigils + 1 negative).
// ======================================================================

export const STONE_NODES: Record<Rarity, number> = { normal: 4, uncommon: 5, rare: 6, epic: 8, legendary: 9, relic: 10, ancient: 10, mythic: 10 };

export function makeStone(rng: Rng, rarity: Rarity = 'epic'): StoneItem {
  const pos = rng.shuffle([...COMBAT_SIGILS]).slice(0, 2);
  const neg = rng.pick(NEGATIVE_SIGILS);
  const n = STONE_NODES[rarity];
  const mk = (sigil: string, negative: boolean): StoneLine => ({ sigil, negative, nodes: Array.from({ length: n }, () => null) });
  return { uid: newUid('stone'), kind: 'stone', name: `${rarity[0]!.toUpperCase()}${rarity.slice(1)} Rune Stone`, rarity, chance: 0.75, lines: [mk(pos[0] as string, false), mk(pos[1] as string, false), mk(neg, true)], bound: true };
}

/** Etches the next empty node of a line. Success lowers the chance by 10%, failure raises it (clamped 25–75%). */
export function etch(stone: StoneItem, line: number, rand: () => number): boolean | null {
  const l = stone.lines[line];
  if (!l) return null;
  const idx = l.nodes.indexOf(null);
  if (idx < 0) return null;
  const ok = rand() < stone.chance;
  l.nodes[idx] = ok;
  stone.chance = Math.min(0.75, Math.max(0.25, stone.chance + (ok ? -0.1 : 0.1)));
  return ok;
}

export function stoneSigilPoints(stone: StoneItem): { id: string; pts: number }[] {
  return stone.lines.map((l) => ({ id: l.sigil, pts: stonePoints(l) }));
}

// ======================================================================
// GEMS: damage (+3% per level) and cooldown (-2% per level) for one skill.
// ======================================================================

export const GEM_MAX_LEVEL = 7;
export const GEM_SOCKETS = 6;
export const gemItemId = (type: 'dmg' | 'cd', level: number): string => `gem_${type}_${level}`;
export const gemEffect = (type: 'dmg' | 'cd', level: number): number => (type === 'dmg' ? 0.03 * level : 0.02 * level);

export interface GemSocket {
  type: 'dmg' | 'cd';
  level: number;
  skillId: string;
}

// ======================================================================
// RELIC CARDS: 6-card deck; duplicates awaken a card (0..5); sets.
// ======================================================================

export interface CardDef {
  id: string;
  name: string;
  rarity: Rarity;
  set: string;
  /** Portrait tint for the procedural card art. */
  hue: number;
}

export interface CardSetDef {
  id: string;
  name: string;
  /** Bonuses at total awakening thresholds (with at least `pieces` cards of the set equipped). */
  bonuses: { pieces: number; awaken: number; text: string; mods: { dmg?: number; hp?: number; dmgReduction?: number; crit?: number } }[];
}

export const CARDS: Record<string, CardDef> = {};
const card = (id: string, name: string, rarity: Rarity, set: string, hue: number) => {
  CARDS[id] = { id, name, rarity, set, hue };
};
card('c_brenna', 'Captain Brenna', 'rare', 'vanguard', 0xc8a060);
card('c_lyra', 'Lyra the Scout', 'uncommon', 'vanguard', 0x6ad060);
card('c_aldara', 'High Warden Aldara', 'legendary', 'lantern', 0xffe28a);
card('c_elowen', 'Elowen of the Rifts', 'epic', 'lantern', 0xc070ff);
card('c_borin', 'Borin Forgehand', 'rare', 'vanguard', 0xff7a3a);
card('c_zahra', 'Zahra the Seer', 'epic', 'lantern', 0x6fd6ff);
card('c_vira', 'Abbess Vira', 'epic', 'lantern', 0xe8eef5);
card('c_hadi', 'Caravan Master Hadi', 'rare', 'wanderer', 0xd0a060);
card('c_bjorn', 'Bjorn the Hunter', 'rare', 'wanderer', 0x9ab8d0);
card('c_lio', 'Lio the Minstrel', 'uncommon', 'wanderer', 0xffb0d0);
card('c_chieftain', 'Grubling Chieftain', 'rare', 'hollow', 0x8a6a3a);
card('c_warden', 'Sandbound Warden', 'epic', 'hollow', 0xd0b070);
card('c_matriarch', 'Frost Matriarch', 'epic', 'hollow', 0x9fe8ff);
card('c_cinderhide', 'Cinderhide Behemoth', 'legendary', 'colossi', 0xff5020);
card('c_gloomfang', 'Gloomfang', 'legendary', 'colossi', 0x8050ff);
card('c_rimebound', 'Rimebound Wyrm', 'legendary', 'colossi', 0x7ad8ff);
card('c_dunestrider', 'Dunestrider', 'legendary', 'colossi', 0xffd040);
card('c_tyrant', "Vhal'Zeroth", 'relic', 'hollow', 0xff2010);

export const CARD_SETS: Record<string, CardSetDef> = {
  lantern: {
    id: 'lantern',
    name: "Lantern's Light",
    bonuses: [
      { pieces: 2, awaken: 0, text: 'Max HP +4%', mods: { hp: 0.04 } },
      { pieces: 4, awaken: 12, text: 'Damage +5%', mods: { dmg: 0.05 } },
      { pieces: 4, awaken: 18, text: 'Damage +8%', mods: { dmg: 0.08 } },
    ],
  },
  vanguard: {
    id: 'vanguard',
    name: 'Vanguard Oath',
    bonuses: [
      { pieces: 2, awaken: 0, text: 'Damage taken -4%', mods: { dmgReduction: 0.04 } },
      { pieces: 3, awaken: 9, text: 'Max HP +8%', mods: { hp: 0.08 } },
    ],
  },
  wanderer: {
    id: 'wanderer',
    name: "Wanderer's Road",
    bonuses: [
      { pieces: 2, awaken: 0, text: 'Crit rate +3%', mods: { crit: 0.03 } },
      { pieces: 3, awaken: 9, text: 'Crit rate +6%', mods: { crit: 0.06 } },
    ],
  },
  hollow: {
    id: 'hollow',
    name: 'Echoes of the Hollow',
    bonuses: [
      { pieces: 2, awaken: 0, text: 'Damage +2%', mods: { dmg: 0.02 } },
      { pieces: 4, awaken: 12, text: 'Damage +6%', mods: { dmg: 0.06 } },
    ],
  },
  colossi: {
    id: 'colossi',
    name: 'Colossus Slayer',
    bonuses: [
      { pieces: 2, awaken: 0, text: 'Max HP +5%', mods: { hp: 0.05 } },
      { pieces: 4, awaken: 12, text: 'Damage +7%', mods: { dmg: 0.07 } },
      { pieces: 4, awaken: 20, text: 'Damage +12%', mods: { dmg: 0.12 } },
    ],
  },
};

export const CARD_DECK_SIZE = 6;
export const MAX_AWAKEN = 5;
/** Duplicates needed to raise awakening from n to n+1. */
export const awakenCost = (n: number): number => n + 1;

export interface CardCollection {
  /** Cards owned: awakening level (0..5). */
  owned: Record<string, number>;
  /** Unspent duplicates per card. */
  dupes: Record<string, number>;
  deck: (string | null)[];
}

export function cardSetBonuses(col: CardCollection): { text: string; mods: CardSetDef['bonuses'][number]['mods'] }[] {
  const out: { text: string; mods: CardSetDef['bonuses'][number]['mods'] }[] = [];
  const bySet = new Map<string, { n: number; awaken: number }>();
  for (const id of col.deck) {
    if (!id) continue;
    const c = CARDS[id];
    if (!c) continue;
    const e = bySet.get(c.set) ?? { n: 0, awaken: 0 };
    e.n++;
    e.awaken += col.owned[id] ?? 0;
    bySet.set(c.set, e);
  }
  for (const [setId, e] of bySet) {
    const set = CARD_SETS[setId];
    if (!set) continue;
    for (const b of set.bonuses) if (e.n >= b.pieces && e.awaken >= b.awaken) out.push({ text: `${set.name}: ${b.text}`, mods: b.mods });
  }
  return out;
}

/** Collection bonus: every distinct card owned grants a little max HP. */
export const collectionHpBonus = (col: CardCollection): number => Object.keys(col.owned).length * 0.002;

export function addCard(col: CardCollection, id: string): 'new' | 'dupe' {
  if (col.owned[id] === undefined) {
    col.owned[id] = 0;
    return 'new';
  }
  col.dupes[id] = (col.dupes[id] ?? 0) + 1;
  return 'dupe';
}

export function awakenCard(col: CardCollection, id: string): boolean {
  const lvl = col.owned[id];
  if (lvl === undefined || lvl >= MAX_AWAKEN) return false;
  const cost = awakenCost(lvl);
  if ((col.dupes[id] ?? 0) < cost) return false;
  col.dupes[id] = (col.dupes[id] ?? 0) - cost;
  col.owned[id] = lvl + 1;
  return true;
}
